import {
  Icon,
  SidebarMenuButton,
  SidebarMenuItem,
  Text,
} from "@salestrak/ui";
import React from "react";

import { SidebarSection } from "@salestrak/ui";
import { useSidebarNav } from "../use-sidebar-nav";
import { useAppData } from "#app/lib/data";
import { usePermission } from "#app/lib/hooks/use-permission";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { useCloseSidebarIfMobile } from "#app/lib/mobile-sidebar-context";
import { CreateTeamDialog } from "#app/components/teams/create-team-dialog";
import { EntityTag } from "#app/components/entity";

/**
 * "Teams" module-menu section — every team the viewer can access (all accessible
 * teams, not membership-only). The `useWorkspaceData().teams` set is already
 * visibility-scoped by the API. Rows navigate to the team's dashboard
 * (`/team/[teamId]`); the create affordances (header "+" and the "Create team"
 * row) open CreateTeamDialog, gated on the `manage.teams` permission (the same
 * key the createTeam mutation enforces server-side).
 */
export function MyTeamsSection() {
  const { navigate, isActive } = useSidebarNav();
  const { teams } = useWorkspaceData();
  const { refetch: refetchAppData } = useAppData();
  const canCreateTeam = usePermission("manage.teams");
  const closeSidebar = useCloseSidebarIfMobile();
  const [createTeamOpen, setCreateTeamOpen] = React.useState(false);

  const openCreateTeam = () => {
    closeSidebar();
    setCreateTeamOpen(true);
  };

  // With no teams AND no create permission there's nothing to show or do —
  // hide the whole section rather than render an empty, actionless accordion.
  // (No teams + can create → keep it for the CTA.)
  if (teams.length === 0 && !canCreateTeam) return null;

  return (
    <>
      <SidebarSection
        // Header "+" — same flow as the Create team row below; omitted
        // entirely without create permission.
        action={
          canCreateTeam
            ? { icon: "plus", label: "Create team", onClick: openCreateTeam }
            : undefined
        }
        title="Teams"
      >
        {teams.map((team, index) => (
          <SidebarMenuItem key={`${team.teamId}-${index}`}>
            <SidebarMenuButton
              isActive={isActive(`/team/${team.teamId}`)}
              onClick={() => {
                closeSidebar();
                navigate(`/team/${team.teamId}`);
              }}
            >
              <EntityTag
                entity={{ id: team.teamId, kind: "team", team }}
                interactive={false}
                weight="regular"
              />
            </SidebarMenuButton>
          </SidebarMenuItem>
        ))}
        {canCreateTeam && (
          <SidebarMenuItem>
            <SidebarMenuButton onClick={openCreateTeam}>
              <Icon name="plus" />
              <Text truncate>Create team</Text>
            </SidebarMenuButton>
          </SidebarMenuItem>
        )}
      </SidebarSection>
      {canCreateTeam && (
        <CreateTeamDialog
          onCreated={async (teamId) => {
            // The module menu's teams come from app data (not TeamsListData,
            // which createTeam already refetches) — refresh, then land on
            // the new team's settings page like the workflow flow does.
            await refetchAppData();
            navigate(`/settings/teams/${teamId}`);
          }}
          onOpenChange={setCreateTeamOpen}
          open={createTeamOpen}
        />
      )}
    </>
  );
}
